import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { Passkey, PasskeyList } from '@lk/shared';
import { forgetLocalPasskey } from '../../lib/device';
import { useMe } from './useAuth';
import { deletePasskey, fetchPasskeys, registerPasskey } from './webauthn';

/**
 * Ключ кеша з акаунтом усередині — і це не педантизм. На спільному телефоні
 * дитина заходить після батька в тій самій вкладці; при спільному ключі її
 * арена встигала б показати «увімкнено» від БАТЬКІВСЬКИХ ключів, поки їде
 * запит. Вихід кеш не чистить (черга відповідей його переживає), тому
 * розділяємо тут.
 */
const passkeysKey = (userId: string): readonly unknown[] => ['passkeys', userId];

/**
 * Ключі поточного акаунта — спільний стан для двох дуже різних екранів:
 * кабінету батьків і арени дитини.
 *
 * Спільна тут саме робота з даними, а не вигляд: список свій у кожної ролі
 * (сервер віддає ключі того, чия сесія), а от правила однакові — після
 * будь-якої зміни в кеші має лежати список, який щойно повернув сервер, і
 * локальний прапорець «на цьому пристрої є ключ» має гаснути разом з
 * останнім ключем, інакше екран входу обіцяв би кнопку, за якою нічого немає.
 */
export interface PasskeysState {
  list: Passkey[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  add: UseMutationResult<PasskeyList, Error, void>;
  remove: UseMutationResult<PasskeyList, Error, string>;
  /** Прибрати ВСІ ключі акаунта — «вимкнути» одним рухом. */
  removeAll: UseMutationResult<PasskeyList, Error, void>;
}

export function usePasskeys({ enabled = true }: { enabled?: boolean } = {}): PasskeysState {
  const queryClient = useQueryClient();
  const me = useMe();
  const userId = me.data?.user.id ?? '';
  const key = passkeysKey(userId);

  const passkeys = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => fetchPasskeys(signal),
    // Без відомого акаунта питати нема про що: сесії ще немає.
    enabled: enabled && userId.length > 0,
  });

  const write = (next: PasskeyList): void => {
    queryClient.setQueryData<PasskeyList>(key, next);
    // Ключів не лишилось ніде — прибираємо й локальну підказку.
    if (next.passkeys.length === 0) forgetLocalPasskey();
  };

  const add = useMutation({ mutationFn: () => registerPasskey(), onSuccess: write });
  const remove = useMutation({ mutationFn: (id: string) => deletePasskey(id), onSuccess: write });

  const removeAll = useMutation({
    mutationFn: async () => {
      /**
       * Список беремо з кеша, а не з замикання: між рендером і натисканням
       * ключі могли змінитися (наприклад, щойно доданий на цьому ж екрані).
       */
      const current = queryClient.getQueryData<PasskeyList>(key)?.passkeys ?? [];
      let last: PasskeyList = { passkeys: [] };
      for (const passkey of current) last = await deletePasskey(passkey.id);
      return last;
    },
    onSuccess: write,
  });

  return {
    list: passkeys.data?.passkeys ?? [],
    isLoading: passkeys.isLoading,
    isError: passkeys.isError,
    error: passkeys.error,
    add,
    remove,
    removeAll,
  };
}
